import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Filter, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  clearPlatformAdminToken,
  getPlatformAdminToken,
  platformAdminApi,
  setPlatformAdminToken,
  type PricingUiConfig,
} from '../lib/api'
import {
  NUDGE_LIBRARY_CATEGORIES,
  NudgeCard,
  NudgePreviewModal,
  normalizeLibraryNudge,
} from '../components/nudges'

const PLANS = ['free', 'starter', 'growth', 'enterprise'] as const

function getCategoryBadgeStyle(category: string) {
  const cat = category.toLowerCase()
  switch (cat) {
    case 'first time manager':
      return { bg: '#FDF5EB', color: '#B5651D', border: '#e8c99a' }
    case 'leadership':
      return { bg: '#FDF5EB', color: '#B5651D', border: '#e8c99a' }
    case 'burnout':
      return { bg: '#fdf2f2', color: '#C0514A', border: '#e8cfcf' }
    case 'inclusion':
      return { bg: '#EBF6FB', color: '#2A7A8A', border: '#a7d8e0' }
    case 'safety':
      return { bg: '#eef5ec', color: '#5C7A3E', border: '#B8C7B1' }
    case 'recognition':
      return { bg: '#EDE8F5', color: '#7B5EA7', border: '#d4cce8' }
    case 'goals':
      return { bg: '#E8F0EE', color: '#3A6B8A', border: '#b0c8d4' }
    default:
      return { bg: '#F5F2EA', color: '#1a3a40', border: '#e0d9cc' }
  }
}

export function PlatformAdminPage() {
  const [tokenInput, setTokenInput] = useState('')
  const [authed, setAuthed] = useState(!!getPlatformAdminToken())
  const [error, setError] = useState<string | null>(null)
  const [workspaces, setWorkspaces] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [detail, setDetail] = useState<{
    workspace: Record<string, unknown>
    members: Record<string, unknown>[]
  } | null>(null)
  const [pricingUi, setPricingUi] = useState<PricingUiConfig | null>(null)
  const [visibleCsv, setVisibleCsv] = useState('')
  const [highlightName, setHighlightName] = useState('')
  const [promoBadge, setPromoBadge] = useState('')
  const [audit, setAudit] = useState<Record<string, unknown>[]>([])

  const [nudgeStats, setNudgeStats] = useState<{
    byStatus: { status: string; count: number }[]
    approvedByCategory: { category: string; count: number }[]
    totalApproved: number
    totalAll: number
  } | null>(null)
  const [nudgeTab, setNudgeTab] = useState<'pending_review' | 'approved' | 'rejected' | 'draft'>('pending_review')
  const [nudgeList, setNudgeList] = useState<Record<string, unknown>[]>([])
  const [nudgeLoading, setNudgeLoading] = useState(false)
  const [nudgeActionId, setNudgeActionId] = useState<string | null>(null)
  const [selectedNudges, setSelectedNudges] = useState<string[]>([])
  const [bulkApproving, setBulkApproving] = useState(false)

  const [auditFilter, setAuditFilter] = useState('')
  const [auditLoading, setAuditLoading] = useState(false)

  const [portalTab, setPortalTab] = useState<'operations' | 'waitlist' | 'nudges' | 'nudge_details'>('operations')
  const [nudgeFilterCategory, setNudgeFilterCategory] = useState<string>('all')
  const [nudgeSearch, setNudgeSearch] = useState('')
  const [nudgePreviewRow, setNudgePreviewRow] = useState<Record<string, unknown> | null>(null)
  const [waitlistRows, setWaitlistRows] = useState<{ email: string; challenge_id: string; created_at: string }[]>([])
  const [waitlistTotal, setWaitlistTotal] = useState(0)
  const [waitlistLoading, setWaitlistLoading] = useState(false)

  // Nudge Details Tab State
  const [detailsSearch, setDetailsSearch] = useState('')
  const [detailsCategory, setDetailsCategory] = useState('all')
  const [detailsSystemTarget, setDetailsSystemTarget] = useState<'all' | 'system1' | 'system2'>('all')
  const [detailsStatus, setDetailsStatus] = useState<string>('all')
  const [detailsNudgeList, setDetailsNudgeList] = useState<Record<string, unknown>[]>([])
  const [detailsLoading, setDetailsLoading] = useState(false)

  const loadWorkspaces = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.listWorkspaces({ limit: 100 })
      setWorkspaces(r.workspaces)
      setTotal(r.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed) return
    loadWorkspaces()
    platformAdminApi
      .getPricingUi()
      .then((r) => {
        const ui = r.pricingUi
        setPricingUi(ui)
        setVisibleCsv(ui?.visiblePlanNames?.join(', ') ?? '')
        setHighlightName(ui?.highlightPlanName ?? '')
        setPromoBadge(ui?.promoBadge ?? '')
      })
      .catch(() => {})
    platformApiLoadAudit()
  }, [authed, loadWorkspaces])

  useEffect(() => {
    if (!authed || portalTab !== 'nudges') return
    platformAdminApi
      .getNudgeStats()
      .then((s) => setNudgeStats(s))
      .catch(() => {})
  }, [authed, portalTab])

  async function platformApiLoadAudit(action?: string) {
    setAuditLoading(true)
    try {
      const r = await platformAdminApi.getAuditFiltered(50, action)
      setAudit(r.entries)
    } catch {
      /* ignore */
    } finally {
      setAuditLoading(false)
    }
  }

  const loadNudgeList = useCallback(async (tab: typeof nudgeTab) => {
    setNudgeLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.listPlatformNudges({ status: tab })
      setNudgeList(r.nudges)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load nudges')
    } finally {
      setNudgeLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed || portalTab !== 'nudges') return
    setSelectedNudges([])
    void loadNudgeList(nudgeTab)
  }, [authed, portalTab, nudgeTab, loadNudgeList])

  async function refreshNudgeData() {
    try {
      const s = await platformAdminApi.getNudgeStats()
      setNudgeStats(s)
    } catch {
      /* ignore */
    }
    await loadNudgeList(nudgeTab)
  }

  const loadWaitlist = useCallback(async () => {
    setWaitlistLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.listWaitlist(2000)
      setWaitlistRows(r.signups)
      setWaitlistTotal(r.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load waitlist')
    } finally {
      setWaitlistLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed || portalTab !== 'waitlist') return
    void loadWaitlist()
  }, [authed, portalTab, loadWaitlist])

  const loadDetailsNudges = useCallback(async (statusFilter: string) => {
    setDetailsLoading(true)
    setError(null)
    try {
      const params: { status?: string } = {}
      if (statusFilter !== 'all') params.status = statusFilter
      const r = await platformAdminApi.listPlatformNudges(params)
      setDetailsNudgeList(r.nudges)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load nudge details')
    } finally {
      setDetailsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed || portalTab !== 'nudge_details') return
    void loadDetailsNudges(detailsStatus)
  }, [authed, portalTab, detailsStatus, loadDetailsNudges])

  const filteredDetailsNudges = useMemo(() => {
    return detailsNudgeList.filter((n) => {
      const cat = String(n.category ?? '').toLowerCase()
      const sys = String(n.system_target ?? '').toLowerCase()
      const title = String(n.title ?? '').toLowerCase()
      const body = String(n.body ?? '').toLowerCase()
      const science = String(n.science_source ?? '').toLowerCase()
      const sourceRefs = String(n.source_refs ?? '').toLowerCase()
      const q = detailsSearch.toLowerCase().trim()

      const matchesCategory = detailsCategory === 'all' || cat === detailsCategory.toLowerCase()
      const matchesSystem = detailsSystemTarget === 'all' || sys === detailsSystemTarget.toLowerCase()
      const matchesQuery =
        !q || title.includes(q) || body.includes(q) || science.includes(q) || sourceRefs.includes(q)

      return matchesCategory && matchesSystem && matchesQuery
    })
  }, [detailsNudgeList, detailsCategory, detailsSystemTarget, detailsSearch])

  async function approveNudge(id: string) {
    setNudgeActionId(id)
    setError(null)
    try {
      await platformAdminApi.patchPlatformNudge(id, { status: 'approved' })
      await refreshNudgeData()
      setNudgePreviewRow((p) => (p && String(p.id) === id ? null : p))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed')
    } finally {
      setNudgeActionId(null)
    }
  }

  async function rejectNudge(id: string) {
    setNudgeActionId(id)
    setError(null)
    try {
      await platformAdminApi.patchPlatformNudge(id, {
        status: 'rejected',
        review_notes: 'Rejected from platform admin',
      })
      await refreshNudgeData()
      setNudgePreviewRow((p) => (p && String(p.id) === id ? null : p))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed')
    } finally {
      setNudgeActionId(null)
    }
  }

  async function handleBulkApprove() {
    if (!selectedNudges.length) return
    setBulkApproving(true)
    setError(null)
    try {
      await platformAdminApi.bulkApproveNudges(selectedNudges)
      const ids = new Set(selectedNudges)
      setSelectedNudges([])
      await refreshNudgeData()
      setNudgePreviewRow((p) => (p && ids.has(String(p.id)) ? null : p))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk approve failed')
    } finally {
      setBulkApproving(false)
    }
  }

  function toggleNudgeSelection(id: string) {
    setSelectedNudges((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const filteredAdminNudges = useMemo(() => {
    return nudgeList.filter((n) => {
      const norm = normalizeLibraryNudge(n)
      if (nudgeFilterCategory !== 'all' && norm.category !== nudgeFilterCategory) return false
      const q = nudgeSearch.trim().toLowerCase()
      if (!q) return true
      return norm.title.toLowerCase().includes(q) || norm.body.toLowerCase().includes(q)
    })
  }, [nudgeList, nudgeFilterCategory, nudgeSearch])

  function login(e: React.FormEvent) {
    e.preventDefault()
    setPlatformAdminToken(tokenInput.trim())
    setAuthed(true)
    setTokenInput('')
    setError(null)
  }

  function logout() {
    clearPlatformAdminToken()
    setAuthed(false)
    setWorkspaces([])
    setDetail(null)
    setPortalTab('operations')
    setWaitlistRows([])
    setWaitlistTotal(0)
  }

  async function expandRow(orgId: string) {
    if (expanded === orgId) {
      setExpanded(null)
      setDetail(null)
      return
    }
    setExpanded(orgId)
    setDetail(null)
    setError(null)
    try {
      const r = await platformAdminApi.getWorkspace(orgId)
      setDetail(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed')
    }
  }

  async function savePlan(orgId: string, plan: string) {
    setError(null)
    try {
      await platformAdminApi.patchWorkspace(orgId, { plan })
      await loadWorkspaces()
      if (detail?.workspace.id === orgId) {
        const r = await platformAdminApi.getWorkspace(orgId)
        setDetail(r)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  async function savePricingUi() {
    setError(null)
    const visiblePlanNames = visibleCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const body: PricingUiConfig = {
      visiblePlanNames: visiblePlanNames.length ? visiblePlanNames : undefined,
      highlightPlanName: highlightName.trim() || null,
      promoBadge: promoBadge.trim() || null,
    }
    try {
      const r = await platformAdminApi.patchPricingUi(body)
      setPricingUi(r.pricingUi)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  async function clearPricingUi() {
    setError(null)
    try {
      await platformAdminApi.patchPricingUi({})
      setVisibleCsv('')
      setHighlightName('')
      setPromoBadge('')
      setPricingUi(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-warm-base">
        <form onSubmit={login} className="w-full max-w-md space-y-6 bg-warm-card border border-warm-border rounded-2xl p-8 shadow-card">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
            <p className="text-sm text-ink-soft">
              Access the Duva global control plane.
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs font-medium text-center">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">
                Platform Admin Secret
              </label>
              <input
                type="password"
                autoComplete="off"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                className="w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-teal outline-none"
                placeholder="Enter Bearer token..."
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-2.5 rounded-full text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity"
              style={{ background: '#2A7A8A' }}
            >
              Authenticate
            </button>
          </div>
          
          <p className="text-[10px] text-center text-ink-muted">
            Tokens are stored locally for this session.
          </p>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-base p-6 md:p-10 text-ink">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold">Platform admin</h1>
            <Link to="/admin/studio" className="text-sm text-brand-teal font-medium hover:underline w-fit">
              Nudge Studio →
            </Link>
          </div>
          <button
            type="button"
            onClick={logout}
            className="text-sm text-ink-muted underline"
          >
            Clear token
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-2">{error}</div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-warm-border pb-3">
          {(
            [
              { id: 'operations' as const, label: 'Operations' },
              { id: 'nudges' as const, label: 'Nudge library (Queue)' },
              { id: 'nudge_details' as const, label: 'Nudge Details (Full Cards)' },
              { id: 'waitlist' as const, label: 'Enterprise waitlist' },
            ] as const
          ).map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setPortalTab(id)}
              className={`text-sm px-4 py-2 rounded-full border font-medium ${
                portalTab === id
                  ? 'bg-brand-teal text-white border-brand-teal'
                  : 'border-warm-border text-ink-muted hover:bg-warm-inset'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {portalTab === 'waitlist' ? (
          <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card overflow-x-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold">Enterprise waitlist</h2>
                <p className="text-xs text-ink-muted mt-1">
                  Emails from the marketing site enterprise waitlist ({waitlistTotal} total
                  {waitlistRows.length < waitlistTotal ? `, showing ${waitlistRows.length}` : ''}).
                </p>
              </div>
              <button
                type="button"
                onClick={() => void loadWaitlist()}
                disabled={waitlistLoading}
                className="text-xs text-brand-teal font-medium"
              >
                {waitlistLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {waitlistLoading && waitlistRows.length === 0 ? (
              <p className="text-xs text-ink-muted">Loading…</p>
            ) : (
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-warm-border">
                    <th className="pb-2 pr-4">Email</th>
                    <th className="pb-2 pr-4">Challenge focus</th>
                    <th className="pb-2">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlistRows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-6 text-ink-muted">
                        No signups yet.
                      </td>
                    </tr>
                  )}
                  {waitlistRows.map((row) => (
                    <tr key={row.email} className="border-b border-warm-border/60">
                      <td className="py-2 pr-4 font-medium break-all">{row.email}</td>
                      <td className="py-2 pr-4 capitalize text-ink-muted">
                        {row.challenge_id?.trim() ? row.challenge_id.replace(/_/g, ' ') : '—'}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ) : null}

        {portalTab === 'nudges' ? (
          <>
            <div className="max-w-5xl mx-auto space-y-6">
              <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-ink" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      Nudge library
                    </h2>
                    <p className="text-xs text-ink-muted mt-1">Review templates like the dashboard library, with platform controls.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link to="/admin/studio" className="text-xs text-brand-teal font-medium hover:underline">
                      Nudge Studio →
                    </Link>
                    <button
                      type="button"
                      onClick={() => void refreshNudgeData()}
                      className="text-xs text-brand-teal font-medium"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
                {nudgeStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border">
                      <p className="text-ink-muted">Active (approved)</p>
                      <p className="text-lg font-semibold text-ink">{nudgeStats.totalApproved}</p>
                    </div>
                    <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border">
                      <p className="text-ink-muted">Total rows</p>
                      <p className="text-lg font-semibold text-ink">{nudgeStats.totalAll}</p>
                    </div>
                    <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border col-span-2">
                      <p className="text-ink-muted mb-1">By status</p>
                      <p className="text-ink font-mono">
                        {(nudgeStats.byStatus ?? []).map((r) => `${r.status}: ${r.count}`).join(' · ') || '—'}
                      </p>
                    </div>
                    <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border col-span-2 sm:col-span-4">
                      <p className="text-ink-muted mb-1">Approved by category</p>
                      <p className="text-ink text-[11px]">
                        {(nudgeStats.approvedByCategory ?? []).map((r) => `${r.category}: ${r.count}`).join(' · ') || '—'}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    {(['pending_review', 'approved', 'rejected', 'draft'] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setNudgeTab(t)}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          nudgeTab === t ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                  {nudgeTab === 'pending_review' && selectedNudges.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => void handleBulkApprove()}
                      disabled={bulkApproving}
                      className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-md hover:bg-green-700 disabled:opacity-50 transition-all shadow-sm"
                    >
                      {bulkApproving ? 'Approving...' : `Bulk approve (${selectedNudges.length})`}
                    </button>
                  ) : null}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                    <input
                      type="text"
                      placeholder="Search nudges…"
                      value={nudgeSearch}
                      onChange={(e) => setNudgeSearch(e.target.value)}
                      className="w-full bg-warm-inset border border-warm-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {NUDGE_LIBRARY_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setNudgeFilterCategory(cat)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors border"
                        style={
                          nudgeFilterCategory === cat
                            ? {
                                background: '#2A7A8A',
                                color: '#fff',
                                borderColor: '#2A7A8A',
                                boxShadow: '0 0 20px -6px rgba(42,122,138,0.4)',
                              }
                            : {
                                background: '#FDFCF8',
                                color: '#6b8a8f',
                                borderColor: '#e0d9cc',
                              }
                        }
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-ink-soft flex items-center gap-1.5">
                  <Filter size={12} />
                  {filteredAdminNudges.length} nudge{filteredAdminNudges.length !== 1 ? 's' : ''} in this queue
                  {nudgeSearch.trim() || nudgeFilterCategory !== 'all' ? ' (filtered)' : ''}
                </p>

                {nudgeLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="bg-warm-inset border border-warm-border rounded-2xl p-5 h-36 animate-pulse shadow-card"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {filteredAdminNudges.length === 0 ? (
                      <div className="col-span-2 text-center py-12 text-ink-muted text-sm">
                        {nudgeList.length === 0 ? 'No nudges in this queue.' : 'No nudges match your filters.'}
                      </div>
                    ) : (
                      filteredAdminNudges.map((n) => {
                        const id = String(n.id)
                        const norm = normalizeLibraryNudge(n)
                        return (
                          <NudgeCard
                            key={id}
                            nudge={norm}
                            onClick={() => setNudgePreviewRow(n)}
                            leading={
                              nudgeTab === 'pending_review' ? (
                                <input
                                  type="checkbox"
                                  checked={selectedNudges.includes(id)}
                                  onChange={() => toggleNudgeSelection(id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  aria-label={`Select ${norm.title}`}
                                />
                              ) : undefined
                            }
                          />
                        )
                      })
                    )}
                  </div>
                )}
              </section>
            </div>
            {nudgePreviewRow ? (
              <NudgePreviewModal
                nudge={normalizeLibraryNudge(nudgePreviewRow)}
                onClose={() => setNudgePreviewRow(null)}
                sourceRefs={nudgePreviewRow.source_refs}
                wide
                pendingReviewActions={
                  nudgeTab === 'pending_review'
                    ? {
                        onApprove: () => void approveNudge(String(nudgePreviewRow.id)),
                        onReject: () => void rejectNudge(String(nudgePreviewRow.id)),
                        busy: nudgeActionId === String(nudgePreviewRow.id),
                      }
                    : undefined
                }
              />
            ) : null}
          </>
        ) : null}

        {portalTab === 'nudge_details' ? (
          <div className="space-y-6">
            <div className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-ink" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Nudge Details
                  </h2>
                  <p className="text-xs text-ink-muted mt-1">
                    Complete science-backed prompts, full APA references, and System 1 vs. System 2 behavioral breakdown.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void loadDetailsNudges(detailsStatus)}
                  disabled={detailsLoading}
                  className="px-4 py-2 rounded-full text-xs font-semibold text-white transition-opacity"
                  style={{ background: '#2A7A8A' }}
                >
                  {detailsLoading ? 'Refreshing…' : 'Refresh list'}
                </button>
              </div>

              {/* Control Bar */}
              <div className="space-y-4 pt-2 border-t border-warm-border">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 bg-warm-inset p-1 rounded-xl border border-warm-border">
                    <span className="text-xs font-semibold text-ink-muted px-2">System Target:</span>
                    {(['all', 'system1', 'system2'] as const).map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setDetailsSystemTarget(st)}
                        className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                          detailsSystemTarget === st
                            ? st === 'system1'
                              ? 'bg-[#2A7A8A] text-white shadow-sm'
                              : st === 'system2'
                              ? 'bg-[#7B5EA7] text-white shadow-sm'
                              : 'bg-brand-teal text-white shadow-sm'
                            : 'text-ink-muted hover:text-ink'
                        }`}
                      >
                        {st === 'all' ? 'All Systems' : st === 'system1' ? 'System 1 (Implicit)' : 'System 2 (Reflective)'}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-1 bg-warm-inset p-1 rounded-xl border border-warm-border">
                    <span className="text-xs font-semibold text-ink-muted px-2">Status:</span>
                    {(['all', 'pending_review', 'approved', 'draft', 'rejected'] as const).map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setDetailsStatus(st)}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-lg capitalize transition-colors ${
                          detailsStatus === st ? 'bg-white shadow-sm text-ink font-bold' : 'text-ink-muted hover:text-ink'
                        }`}
                      >
                        {st.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search & Category Pills */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                    <input
                      type="text"
                      placeholder="Search title, prompt, science, or reference citation…"
                      value={detailsSearch}
                      onChange={(e) => setDetailsSearch(e.target.value)}
                      className="w-full bg-warm-inset border border-warm-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {NUDGE_LIBRARY_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setDetailsCategory(cat)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors border"
                        style={
                          detailsCategory === cat
                            ? { background: '#2A7A8A', color: '#fff', borderColor: '#2A7A8A' }
                            : { background: '#FDFCF8', color: '#6b8a8f', borderColor: '#e0d9cc' }
                        }
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-ink-soft flex items-center gap-1.5 pt-1">
                  <Filter size={12} />
                  Showing {filteredDetailsNudges.length} nudge{filteredDetailsNudges.length !== 1 ? 's' : ''}
                  {detailsSearch.trim() || detailsCategory !== 'all' || detailsSystemTarget !== 'all' || detailsStatus !== 'all'
                    ? ' (filtered)'
                    : ''}
                </p>
              </div>

              {/* Cards Grid */}
              {detailsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="bg-warm-inset border border-warm-border rounded-2xl p-6 h-56 animate-pulse" />
                  ))}
                </div>
              ) : filteredDetailsNudges.length === 0 ? (
                <div className="text-center py-16 text-ink-muted text-sm bg-warm-inset rounded-2xl border border-warm-border">
                  No nudges match your current search and filter selections.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {filteredDetailsNudges.map((n) => {
                    const id = String(n.id)
                    const cat = String(n.category ?? 'general')
                    const sys = String(n.system_target ?? 'system1').toLowerCase()
                    const title = String(n.title ?? '')
                    const body = String(n.body ?? '')
                    const science = String(n.science_source ?? '')
                    const score = Number(n.effectiveness_score ?? 0.8)
                    const status = String(n.status ?? 'approved')
                    
                    let parsedRef = ''
                    if (n.source_refs) {
                      try {
                        const arr = JSON.parse(String(n.source_refs))
                        if (Array.isArray(arr) && arr.length > 0) {
                          parsedRef = arr.join('; ')
                        } else {
                          parsedRef = String(n.source_refs)
                        }
                      } catch {
                        parsedRef = String(n.source_refs)
                      }
                    }

                    const catStyle = getCategoryBadgeStyle(cat)
                    const dotColor = score >= 0.85 ? '#5C7A3E' : score >= 0.75 ? '#D4A017' : '#C0514A'

                    return (
                      <div
                        key={id}
                        className="bg-[#FDFCF8] border border-[#e0d9cc] rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <span
                              className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border capitalize"
                              style={{
                                background: catStyle.bg,
                                color: catStyle.color,
                                borderColor: catStyle.border,
                              }}
                            >
                              {cat}
                            </span>
                            <div className="flex items-center gap-2">
                              {status !== 'approved' && (
                                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                  {status.replace('_', ' ')}
                                </span>
                              )}
                              <span
                                className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                                  sys === 'system2'
                                    ? 'bg-[rgba(123,94,167,0.10)] text-[#7B5EA7] border-[rgba(123,94,167,0.25)]'
                                    : 'bg-[rgba(42,122,138,0.12)] text-[#2A7A8A] border-[rgba(42,122,138,0.25)]'
                                }`}
                              >
                                {sys === 'system2' ? 'System 2' : 'System 1'}
                              </span>
                            </div>
                          </div>

                          <h3 className="text-base font-semibold text-[#1a3a40] leading-snug mb-2">{title}</h3>
                          <p className="text-xs text-[#3d6068] leading-relaxed mb-4 whitespace-pre-wrap">{body}</p>
                        </div>

                        <div>
                          <div className="flex items-end justify-between gap-3 pt-3 border-t border-[#e0d9cc]/60">
                            <div className="text-[11px] italic text-[#6b8a8f] leading-tight flex-1">
                              {science || 'Peer-reviewed research source'}
                            </div>
                            <div className="text-[11px] font-bold text-[#6b8a8f] whitespace-nowrap flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full inline-block" style={{ background: dotColor }} />
                              {Math.round(score * 10)}/10
                            </div>
                          </div>

                          {parsedRef && (
                            <div className="text-[10px] text-[#6b8a8f] mt-3 pt-2.5 border-t border-[#e0d9cc] leading-normal font-sans">
                              <span className="font-semibold text-ink-muted">Citation: </span>
                              {parsedRef.replace(/^\["|"\]$/g, '')}
                            </div>
                          )}

                          {status === 'pending_review' && (
                            <div className="flex gap-2 mt-4 pt-3 border-t border-[#e0d9cc] justify-end">
                              <button
                                type="button"
                                onClick={async () => {
                                  await approveNudge(id)
                                  void loadDetailsNudges(detailsStatus)
                                }}
                                className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  await rejectNudge(id)
                                  void loadDetailsNudges(detailsStatus)
                                }}
                                className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-200 transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {portalTab === 'operations' ? (
          <>
        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card">
          <h2 className="text-sm font-semibold mb-3">Pricing page overrides</h2>
          <p className="text-xs text-ink-muted mb-4">
            Optional. Leave visible plans empty to show all tiers. Plan names: Free, Starter, Growth, Enterprise (comma-separated).
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-xs">
              <span className="text-ink-muted">Visible plan names (CSV)</span>
              <input
                value={visibleCsv}
                onChange={(e) => setVisibleCsv(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Free, Starter, Growth, Enterprise"
              />
            </label>
            <label className="block text-xs">
              <span className="text-ink-muted">Highlight plan name</span>
              <input
                value={highlightName}
                onChange={(e) => setHighlightName(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Growth"
              />
            </label>
            <label className="block text-xs md:col-span-2">
              <span className="text-ink-muted">Promo badge (short text)</span>
              <input
                value={promoBadge}
                onChange={(e) => setPromoBadge(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Founding teams — extra seats"
              />
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={savePricingUi}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white"
              style={{ background: '#2A7A8A' }}
            >
              Save pricing UI
            </button>
            <button type="button" onClick={clearPricingUi} className="px-4 py-2 rounded-full text-sm border border-warm-border">
              Reset to defaults
            </button>
          </div>
          {pricingUi && (
            <pre className="mt-3 text-[10px] bg-warm-inset p-2 rounded overflow-auto">{JSON.stringify(pricingUi, null, 2)}</pre>
          )}
        </section>

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold">Workspaces ({total})</h2>
            <button
              type="button"
              onClick={loadWorkspaces}
              disabled={loading}
              className="text-xs text-brand-teal font-medium"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-ink-muted border-b border-warm-border">
                <th className="pb-2 pr-2">Name</th>
                <th className="pb-2 pr-2">Plan</th>
                <th className="pb-2 pr-2">Activity (7d)</th>
                <th className="pb-2 pr-2">Members</th>
                <th className="pb-2 pr-2">Country</th>
                <th className="pb-2 pr-2">Industry</th>
                <th className="pb-2 pr-2">Last activity</th>
                <th className="pb-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((w) => {
                const id = String(w.id)
                return (
                  <Fragment key={id}>
                    <tr className="border-b border-warm-border/60">
                      <td className="py-2 pr-2 font-medium">
                        <button type="button" className="text-left underline text-brand-teal" onClick={() => expandRow(id)}>
                          {String(w.name ?? id)}
                        </button>
                      </td>
                      <td className="py-2 pr-2 capitalize">{String(w.plan ?? '')}</td>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              Boolean(w.slack_connected) ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={Boolean(w.slack_connected) ? 'Slack Connected' : 'Slack Not Connected'}
                          />
                          <span className="text-xs font-medium text-gray-600">
                            {Number(w.deliveries_7d ?? 0)} sends
                          </span>
                          {Number(w.deliveries_7d ?? 0) === 0 && Boolean(w.slack_connected) && (
                            <span className="text-[10px] text-red-500 font-bold">STALLED</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-2">{String(w.live_member_count ?? w.employee_count ?? '—')}</td>
                      <td className="py-2 pr-2">{String(w.signup_country ?? '—')}</td>
                      <td className="py-2 pr-2">{String(w.industry ?? '—')}</td>
                      <td className="py-2 pr-2">{w.last_activity_at ? String(w.last_activity_at).slice(0, 10) : '—'}</td>
                      <td className="py-2">{String(w.created_at ?? '').slice(0, 10)}</td>
                    </tr>
                    {expanded === id && detail && (
                      <tr>
                        <td colSpan={7} className="pb-4 pt-0 bg-warm-inset/50 px-3 py-3">
                          <div className="flex flex-wrap gap-3 items-center mb-3">
                            <span className="text-ink-muted">Set plan:</span>
                            {PLANS.map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => savePlan(id, p)}
                                className="px-2 py-1 rounded border border-warm-border text-[11px] capitalize hover:bg-warm-card"
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                          <p className="text-ink-muted mb-2">{detail.members.length} members</p>
                          <ul className="max-h-40 overflow-auto text-[11px] space-y-1">
                            {detail.members.map((m) => (
                              <li key={String(m.id)}>
                                {String(m.name)} — {String(m.email)} ({String(m.role)})
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </section>

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold">Platform audit</h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Filter by action..."
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
                className="px-2 py-1 text-[10px] bg-warm-inset border border-warm-border rounded-md focus:ring-1 focus:ring-brand-teal outline-none w-32"
              />
              <button
                type="button"
                onClick={() => void platformApiLoadAudit(auditFilter.trim() || undefined)}
                disabled={auditLoading}
                className="px-2 py-1 text-[10px] font-medium text-brand-teal border border-brand-teal rounded hover:bg-brand-teal/5 disabled:opacity-50"
              >
                {auditLoading ? '...' : 'Filter'}
              </button>
            </div>
          </div>
          <ul className="text-[11px] space-y-2 font-mono text-ink-muted max-h-48 overflow-auto">
            {audit.length === 0 && <li className="italic">No audit events found.</li>}
            {audit.map((a) => (
              <li key={String(a.id)}>
                <span className="text-brand-teal">{String(a.created_at).slice(0, 19).replace('T', ' ')}</span> —{' '}
                <span className="text-ink font-bold">{String(a.action)}</span>{' '}
                <span className="opacity-70">
                  {String(a.resource_type)} {a.resource_id ? `(${String(a.resource_id)})` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
          </>
        ) : null}
      </div>
    </div>
  )
}
